/**
 * Cost calculator — plan limits, add-ons, and recommendation logic.
 * Static catalog matches public pricing / upgrade plan details; API data overrides when available.
 */

export const NEED_TYPES = {
  CHAT: 'chat',
  INBOUND: 'inbound_voice',
  OUTBOUND: 'outbound_voice',
  VOICE_CHAT: 'voice_chat',
};

export const PLAN_API_KEYS = {
  free: 'Free',
  payg: 'Pay_As_You_Go',
  basic: 'Starter_Chat',
  standard: 'Starter_Voice',
  advance: 'Professional',
  pro: 'Enterprise',
};

/** Channel keys used in the calculator UI */
export const CHANNEL_ADDON_CATALOG = {
  whatsapp: {
    addonKey: 'WhatsAppAiAgent',
    label: 'WhatsApp AI Chat Agent',
    price: 199,
    messageLimit: 11000,
  },
  instagram: {
    addonKey: 'InstagramAiAgent',
    label: 'Instagram AI Agent',
    price: 49,
    messageLimit: 11000,
  },
  facebook: {
    addonKey: 'FacebookAiAgent',
    label: 'Facebook AI Agent',
    price: 49,
    messageLimit: 11000,
  },
  email: {
    addonKey: 'EmailAiAgent',
    label: 'Email AI Agent',
    price: 99,
    messageLimit: 50000,
  },
  line: {
    addonKey: 'LineAiAgent',
    label: 'Line AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  sms: {
    addonKey: 'SmsAiAgent',
    label: 'SMS AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  wechat: {
    addonKey: 'WeChatAiAgent',
    label: 'WeChat AI Agent',
    price: 99,
    messageLimit: 11000,
  },
  web: {
    addonKey: null,
    label: 'Website / Web Chat',
    price: 0,
    messageLimit: Infinity,
  },
};

const PLANS_WITH_WEB_CHAT = new Set([
  'Free',
  'Starter_Chat',
  'Professional',
  'Enterprise',
  'Pay_As_You_Go',
]);

/** Static limits shaped like /plan_limit API — used when API is unavailable */
export const CALCULATOR_STATIC_PLAN_LIMITS = {
  Free: {
    PRICE: 0,
    MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT: 22,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 22,
    MAX_INBOUND_CALL_DURATION_MINUTES: 30,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 2,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 2,
    CHARGE_PER_CHAT_SESSION: 0,
    CHARGE_PER_MINUTE: 0,
  },
  Pay_As_You_Go: {
    PRICE: 20,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 22000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 0,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 2,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 2,
    CHARGE_PER_CHAT_SESSION: 0.18,
    CHARGE_PER_SESSION: 0.18,
    CHARGE_PER_MINUTE: 0.28,
    CHARGE_PER_MESSAGE: 0.017,
  },
  Starter_Chat: {
    PRICE: 199,
    MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT: 3000,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 3000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 0,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 0,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 5,
    MAX_CONCURRENT_CALLS: 0,
  },
  Starter_Voice: {
    PRICE: 249,
    MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT: 0,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 0,
    MAX_INBOUND_CALL_DURATION_MINUTES: 500,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 5,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 0,
    MAX_CONCURRENT_CALLS: 3,
  },
  Professional: {
    PRICE: 399,
    MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT: 2000,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 2000,
    MAX_INBOUND_CALL_DURATION_MINUTES: 500,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 0,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 10,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 10,
    MAX_CONCURRENT_CALLS: 3,
  },
  Enterprise: {
    PRICE: 999,
    MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT: 2500,
    MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT: 2500,
    MAX_INBOUND_CALL_DURATION_MINUTES: 1000,
    MAX_OUTBOUND_CALL_DURATION_MINUTES: 1000,
    MAX_VOICE_AI_AGENT_CREATION_LIMIT: 20,
    MAX_CHAT_AI_AGENT_CREATION_LIMIT: 20,
    MAX_CONCURRENT_CALLS: 3,
  },
  Addons: {
    WhatsAppAiAgent: { PRICE: 199, MAX_CHAT_MESSAGES_FOR_WHATSAPP_CHAT_LIMIT: 11000 },
    InstagramAiAgent: { PRICE: 49, MAX_CHAT_MESSAGES_FOR_INSTAGRAM_CHAT_LIMIT: 11000 },
    FacebookAiAgent: { PRICE: 49, MAX_CHAT_MESSAGES_FOR_FACEBOOK_CHAT_LIMIT: 11000 },
    EmailAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_EMAIL_CHAT_LIMIT: 50000 },
    LineAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_LINE_CHAT_LIMIT: 11000 },
    SmsAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_SMS_CHAT_LIMIT: 11000 },
    WeChatAiAgent: { PRICE: 99, MAX_CHAT_MESSAGES_FOR_WECHAT_CHAT_LIMIT: 11000 },
    WhatsAppVoiceAgent: { PRICE: 99, MAX_CALL_DURATION_MINUTES_PER_TENANT: 350 },
    VoiceCallRecording: { PRICE: 49, MAX_CALL_RECORDING_MINUTES: 1000 },
    Starter_Voice_WebChat_Addon: { PRICE: 49 },
  },
};

export const STATIC_PLAN_METRICS = {
  Free: {
    price: 0,
    chatSessions: 22,
    inboundMinutes: 30,
    outboundMinutes: 0,
    voiceAgents: 2,
    chatAgents: 2,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'FREE',
    subtitle: 'Basic access for individuals and testing',
  },
  Pay_As_You_Go: {
    price: 20,
    chatSessions: Infinity,
    inboundMinutes: Infinity,
    outboundMinutes: Infinity,
    voiceAgents: 2,
    chatAgents: 2,
    chargePerChat: 0.18,
    chargePerMinute: 0.28,
    minDeposit: 20,
    displayName: 'PAY AS YOU GO',
    subtitle: 'Flexible pricing based on usage',
  },
  Starter_Chat: {
    price: 199,
    chatSessions: 3000,
    inboundMinutes: 0,
    outboundMinutes: 0,
    voiceAgents: 5,
    chatAgents: 5,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'BASIC',
    subtitle: 'Perfect for small teams getting started with AI chat',
  },
  Starter_Voice: {
    price: 249,
    chatSessions: 0,
    inboundMinutes: 500,
    outboundMinutes: 0,
    voiceAgents: 5,
    chatAgents: 0,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'STANDARD',
    subtitle: 'Voice AI capabilities for customer service',
  },
  Professional: {
    price: 399,
    chatSessions: 2000,
    inboundMinutes: 500,
    outboundMinutes: 0,
    voiceAgents: 10,
    chatAgents: 10,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'ADVANCE',
    subtitle: 'Advanced features for growing businesses',
  },
  Enterprise: {
    price: 999,
    chatSessions: 2500,
    inboundMinutes: 1000,
    outboundMinutes: 1000,
    voiceAgents: 20,
    chatAgents: 20,
    chargePerChat: 0,
    chargePerMinute: 0,
    minDeposit: 0,
    displayName: 'PRO',
    subtitle: 'Complete solution for large organizations',
  },
};

const CHAT_LIMIT_KEYS = [
  'MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT',
  'MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT',
];

const FIXED_PLANS = [
  'Starter_Chat',
  'Starter_Voice',
  'Professional',
  'Enterprise',
];

const PLAN_TIER = {
  Free: 0,
  Starter_Chat: 1,
  Starter_Voice: 2,
  Professional: 3,
  Enterprise: 4,
};

export const INCLUDED_CONCURRENT_CALLS = 3;

const hasPaidChatChannels = (channels = []) =>
  (Array.isArray(channels) ? channels : []).some((channel) => channel !== 'web');

export const anyFixedPlanFitsUsage = (usage, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const channels = usage.channels || [];

  return FIXED_PLANS.some((planKey) => {
    const metrics = extractPlanMetrics(planKey, limits);
    if (!planSupportsNeedType(metrics, usage.needType, usage)) return false;
    if (!usageFitsPlan(usage, metrics)) return false;

    if (
      usage.needType !== NEED_TYPES.INBOUND &&
      channels.includes('web') &&
      !planSupportsWebChat(planKey)
    ) {
      return false;
    }

    return true;
  });
};

export const evaluateSalesContact = (usage, planLimits, concurrentCalls = INCLUDED_CONCURRENT_CALLS) => {
  const reasons = [];

  if (concurrentCalls > INCLUDED_CONCURRENT_CALLS) {
    reasons.push(
      `You need ${formatCount(concurrentCalls)} concurrent calls. Monthly plans include up to ${INCLUDED_CONCURRENT_CALLS}.`
    );
  }

  const fixedFits = anyFixedPlanFitsUsage(usage, planLimits);
  const paidChannels = hasPaidChatChannels(usage.channels);

  if (!fixedFits && paidChannels) {
    reasons.push(
      'Your chat volume exceeds monthly plan limits, and Pay As You Go does not support channel add-ons. Our team can build a custom bundle for you.'
    );
  }

  return {
    needsSales: reasons.length > 0,
    reasons,
  };
};

const parseMoney = (value) => {
  if (value == null || value === '-' || value === '') return null;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const readNumber = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const pickLimit = (apiValue, fallbackValue) => {
  const api = Number(apiValue);
  if (Number.isFinite(api) && api > 0) return api;
  if (fallbackValue === Infinity) return Infinity;
  return Number(fallbackValue) || 0;
};

export const mergePlanLimits = (apiLimits) => {
  const merged = {
    ...CALCULATOR_STATIC_PLAN_LIMITS,
    Addons: { ...CALCULATOR_STATIC_PLAN_LIMITS.Addons },
  };

  if (!apiLimits || typeof apiLimits !== 'object') return merged;

  // Only merge add-on prices from API — plan limits stay on the public catalog
  if (apiLimits.Addons) {
    merged.Addons = { ...merged.Addons, ...apiLimits.Addons };
  }

  return merged;
};

export const extractPlanMetrics = (planKey, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const fallback = STATIC_PLAN_METRICS[planKey] || STATIC_PLAN_METRICS.Free;
  const data = limits[planKey];
  if (!data || typeof data !== 'object') return { ...fallback, planKey };

  const chatSessions = pickLimit(
    CHAT_LIMIT_KEYS.map((k) => data[k]).find((v) => Number(v) > 0),
    fallback.chatSessions
  );

  const inboundMinutes = pickLimit(
    data.MAX_INBOUND_CALL_DURATION_MINUTES ||
      (data.MAX_INBOUND_CALL_DURATION_SECONDS
        ? Number(data.MAX_INBOUND_CALL_DURATION_SECONDS) / 60
        : 0),
    fallback.inboundMinutes
  );

  const outboundMinutes = pickLimit(
    data.MAX_OUTBOUND_CALL_DURATION_MINUTES ||
      (data.MAX_OUTBOUND_CALL_DURATION_SECONDS
        ? Number(data.MAX_OUTBOUND_CALL_DURATION_SECONDS) / 60
        : 0),
    fallback.outboundMinutes
  );

  const chargePerChat =
    parseMoney(data.CHARGE_PER_CHAT_SESSION) ??
    parseMoney(data.CHARGE_PER_SESSION) ??
    fallback.chargePerChat;

  const chargePerMinute =
    parseMoney(data.CHARGE_PER_MINUTE) ??
    parseMoney(data.CHARGE_PER_CALL_MINUTE) ??
    fallback.chargePerMinute;

  const isPayg = planKey === 'Pay_As_You_Go';

  return {
    planKey,
    price: readNumber(data.PRICE, fallback.price),
    chatSessions: isPayg ? Infinity : chatSessions,
    inboundMinutes: isPayg ? Infinity : inboundMinutes,
    outboundMinutes: isPayg ? Infinity : outboundMinutes,
    voiceAgents: readNumber(
      data.MAX_VOICE_AI_AGENT_CREATION_LIMIT,
      data.MAX_AGENT_CREATION_LIMIT,
      fallback.voiceAgents
    ),
    chatAgents: readNumber(data.MAX_CHAT_AI_AGENT_CREATION_LIMIT, fallback.chatAgents),
    chargePerChat,
    chargePerMinute,
    minDeposit: readNumber(data.PRICE, fallback.minDeposit),
    displayName: fallback.displayName,
    subtitle: fallback.subtitle,
  };
};

export const getUsageFlags = (needType) => ({
  usesChat: needType === NEED_TYPES.CHAT || needType === NEED_TYPES.VOICE_CHAT,
  usesInbound: needType === NEED_TYPES.INBOUND || needType === NEED_TYPES.VOICE_CHAT,
  usesOutbound: needType === NEED_TYPES.OUTBOUND || needType === NEED_TYPES.VOICE_CHAT,
});

export const planSupportsNeedType = (metrics, needType, usage = {}) => {
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(needType);
  const chatNeeded = usesChat && (usage.monthlyChats || 0) > 0;
  const inboundNeeded = usesInbound && (usage.inboundMinutes || 0) > 0;
  const outboundNeeded = usesOutbound && (usage.outboundMinutes || 0) > 0;

  if (chatNeeded && metrics.chatSessions <= 0 && metrics.planKey !== 'Pay_As_You_Go') return false;
  if (inboundNeeded && metrics.inboundMinutes <= 0 && metrics.planKey !== 'Pay_As_You_Go') {
    return false;
  }
  if (outboundNeeded && metrics.outboundMinutes <= 0 && metrics.planKey !== 'Pay_As_You_Go') {
    return false;
  }
  return true;
};

export const usageFitsPlan = (usage, metrics) => {
  if (metrics.planKey === 'Pay_As_You_Go') return true;

  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage.needType);

  if (usesChat && usage.monthlyChats > 0 && usage.monthlyChats > metrics.chatSessions) {
    return false;
  }
  if (usesInbound && usage.inboundMinutes > 0 && usage.inboundMinutes > metrics.inboundMinutes) {
    return false;
  }
  if (usesOutbound && usage.outboundMinutes > 0 && usage.outboundMinutes > metrics.outboundMinutes) {
    return false;
  }
  return true;
};

const planSupportsWebChat = (planKey) => PLANS_WITH_WEB_CHAT.has(planKey);

const getAddonPrice = (addonKey, planLimits) => {
  const limits = mergePlanLimits(planLimits);
  const fromApi = limits.Addons?.[addonKey];
  const staticFallback = CALCULATOR_STATIC_PLAN_LIMITS.Addons?.[addonKey];
  return (
    parseMoney(fromApi?.PRICE ?? fromApi?.price) ??
    parseMoney(staticFallback?.PRICE ?? staticFallback?.price) ??
    0
  );
};

/** Monthly add-on cost for selected chat channels on a given base plan */
export const calculateChannelAddonCost = (planKey, channels = [], planLimits) => {
  const selected = Array.isArray(channels) ? channels : [];
  const items = [];
  let cost = 0;

  const needsWeb =
    selected.includes('web') &&
    !planSupportsWebChat(planKey);

  if (needsWeb) {
    const webAddonPrice = getAddonPrice('Starter_Voice_WebChat_Addon', planLimits);
    if (webAddonPrice > 0) {
      cost += webAddonPrice;
      items.push({
        key: 'Starter_Voice_WebChat_Addon',
        label: 'Web Chat Add-on',
        price: webAddonPrice,
      });
    }
  }

  selected.forEach((channelKey) => {
    if (channelKey === 'web') return;
    const catalog = CHANNEL_ADDON_CATALOG[channelKey];
    if (!catalog?.addonKey) return;

    const price = getAddonPrice(catalog.addonKey, planLimits) || catalog.price;
    cost += price;
    items.push({
      key: catalog.addonKey,
      label: catalog.label,
      price,
    });
  });

  return { cost, items };
};

export const calculatePaygMonthlyCost = (usage, metrics, channelAddonCost = 0) => {
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage.needType);
  let cost = 0;
  if (usesChat) cost += usage.monthlyChats * (metrics.chargePerChat || 0.18);
  if (usesInbound) cost += usage.inboundMinutes * (metrics.chargePerMinute || 0.28);
  if (usesOutbound) cost += usage.outboundMinutes * (metrics.chargePerMinute || 0.28);
  return Math.max(cost, metrics.minDeposit || metrics.price || 20) + channelAddonCost;
};

export const calculateFixedPlanCost = (usage, metrics, channelAddonCost = 0) => {
  if (metrics.planKey === 'Free' && metrics.price === 0) return channelAddonCost;
  return (metrics.price || 0) + channelAddonCost;
};

const buildRecommendationNote = ({
  planKey,
  isUsageBased,
  paygCost,
  bestFixed,
  addonItems,
  usage,
}) => {
  if (isUsageBased && bestFixed) {
    return `Pay As You Go is ${formatUsd(paygCost)}/mo at your usage — cheaper than ${bestFixed.metrics.displayName} (${formatUsd(bestFixed.monthlyCost)}/mo).`;
  }

  if (isUsageBased) {
    return 'Your usage exceeds included limits on monthly plans. Pay As You Go is recommended at this volume.';
  }

  if (addonItems.length > 0) {
    const addonNames = addonItems.map((a) => a.label).join(', ');
    return `Includes add-ons for your channels: ${addonNames}.`;
  }

  if (paygCost != null && bestFixed && paygCost < bestFixed.monthlyCost) {
    return `Pay As You Go is ${formatUsd(paygCost)}/mo at this volume — consider it for variable usage.`;
  }

  return null;
};

export const getPlanLimitSummary = (metrics, usage) => {
  const parts = [];
  const { usesChat, usesInbound, usesOutbound } = getUsageFlags(usage?.needType || '');
  const showFullProLimits = metrics.planKey === 'Enterprise';

  if (
    (usesChat || showFullProLimits) &&
    metrics.chatSessions > 0 &&
    metrics.chatSessions !== Infinity
  ) {
    parts.push(`${formatCount(metrics.chatSessions)} chats`);
  }
  if (
    (usesInbound || showFullProLimits) &&
    metrics.inboundMinutes > 0 &&
    metrics.inboundMinutes !== Infinity
  ) {
    parts.push(`${formatCount(metrics.inboundMinutes)} inbound min`);
  }
  if (
    (usesOutbound || showFullProLimits) &&
    metrics.outboundMinutes > 0 &&
    metrics.outboundMinutes !== Infinity
  ) {
    parts.push(`${formatCount(metrics.outboundMinutes)} outbound min`);
  }

  return parts.length ? `Includes ${parts.join(' · ')}` : null;
};

const getPreferredPlan = (usage, fixedCandidates) => {
  const byKey = (key) => fixedCandidates.find((c) => c.planKey === key) || null;
  const outbound = usage.outboundMinutes || 0;
  const cheapestFixed =
    fixedCandidates.length > 0
      ? [...fixedCandidates].sort(
          (a, b) => a.monthlyCost - b.monthlyCost || a.tier - b.tier
        )[0]
      : null;

  if (usage.needType === NEED_TYPES.CHAT) {
    return cheapestFixed;
  }

  if (usage.needType === NEED_TYPES.OUTBOUND) {
    return byKey('Enterprise');
  }

  if (usage.needType === NEED_TYPES.INBOUND) {
    return byKey('Starter_Voice') || byKey('Enterprise');
  }

  if (usage.needType === NEED_TYPES.VOICE_CHAT && outbound > 0) {
    return byKey('Enterprise');
  }

  if (usage.needType === NEED_TYPES.VOICE_CHAT) {
    return cheapestFixed;
  }

  return null;
};

export const recommendPricingPlan = (usage, planLimits, options = {}) => {
  const limits = mergePlanLimits(planLimits);
  const channels = usage.channels || ['web'];
  const concurrentCalls = options.concurrentCalls ?? INCLUDED_CONCURRENT_CALLS;
  const salesContact = evaluateSalesContact(
    { ...usage, channels },
    limits,
    concurrentCalls
  );
  const paygMetrics = extractPlanMetrics('Pay_As_You_Go', limits);
  const paygAddons = { cost: 0, items: [] };
  const paygCost = calculatePaygMonthlyCost(usage, paygMetrics, paygAddons.cost);

  const fixedCandidates = [];

  FIXED_PLANS.forEach((planKey) => {
    const metrics = extractPlanMetrics(planKey, limits);
    if (!planSupportsNeedType(metrics, usage.needType, usage)) return;
    if (!usageFitsPlan(usage, metrics)) return;

    const { cost: addonCost, items: addonItems } = calculateChannelAddonCost(
      planKey,
      channels,
      limits
    );

    if (
      usage.needType !== NEED_TYPES.INBOUND &&
      channels.includes('web') &&
      !planSupportsWebChat(planKey) &&
      addonCost === 0
    ) {
      return;
    }

    const basePlanCost = metrics.planKey === 'Free' ? 0 : metrics.price || 0;
    const monthlyCost = calculateFixedPlanCost(usage, metrics, addonCost);

    fixedCandidates.push({
      planKey,
      metrics,
      basePlanCost,
      addonCost,
      addonItems,
      monthlyCost,
      tier: PLAN_TIER[planKey] ?? 0,
    });
  });

  const bestFixed =
    fixedCandidates.length > 0
      ? [...fixedCandidates].sort(
          (a, b) => a.monthlyCost - b.monthlyCost || a.tier - b.tier
        )[0]
      : null;

  const preferredPlan = getPreferredPlan(usage, fixedCandidates);

  const attachSales = (result) => ({
    ...result,
    salesContact,
  });

  if (preferredPlan) {
    const paygCheaper = paygCost < preferredPlan.monthlyCost;
    return attachSales({
      ...preferredPlan,
      isUsageBased: false,
      paygAlternativeCost: paygCheaper ? paygCost : null,
      fixedAlternative: null,
      limitSummary: getPlanLimitSummary(preferredPlan.metrics, usage),
      note: buildRecommendationNote({
        planKey: preferredPlan.planKey,
        isUsageBased: false,
        paygCost: paygCheaper ? paygCost : null,
        bestFixed: preferredPlan,
        addonItems: preferredPlan.addonItems,
        usage,
      }),
    });
  }

  if (bestFixed && paygCost < bestFixed.monthlyCost) {
    return attachSales({
      planKey: 'Pay_As_You_Go',
      metrics: paygMetrics,
      basePlanCost: paygMetrics.minDeposit || 20,
      addonCost: paygAddons.cost,
      addonItems: paygAddons.items,
      monthlyCost: paygCost,
      isUsageBased: true,
      fixedAlternative: bestFixed,
      limitSummary: null,
      note: buildRecommendationNote({
        planKey: 'Pay_As_You_Go',
        isUsageBased: true,
        paygCost,
        bestFixed,
        addonItems: paygAddons.items,
        usage,
      }),
    });
  }

  if (bestFixed) {
    const paygCheaper = paygCost < bestFixed.monthlyCost;

    return attachSales({
      ...bestFixed,
      isUsageBased: false,
      paygAlternativeCost: paygCheaper ? paygCost : null,
      fixedAlternative: null,
      limitSummary: getPlanLimitSummary(bestFixed.metrics, usage),
      note: buildRecommendationNote({
        planKey: bestFixed.planKey,
        isUsageBased: false,
        paygCost: paygCheaper ? paygCost : null,
        bestFixed,
        addonItems: bestFixed.addonItems,
        usage,
      }),
    });
  }

  return attachSales({
    planKey: 'Pay_As_You_Go',
    metrics: paygMetrics,
    basePlanCost: paygMetrics.minDeposit || 20,
    addonCost: paygAddons.cost,
    addonItems: paygAddons.items,
    monthlyCost: paygCost,
    isUsageBased: true,
    fixedAlternative: null,
    limitSummary: null,
    note: buildRecommendationNote({
      planKey: 'Pay_As_You_Go',
      isUsageBased: true,
      paygCost,
      bestFixed: null,
      addonItems: paygAddons.items,
      usage,
    }),
  });
};

export const formatUsd = (amount) => {
  const value = Math.round(Number(amount || 0));
  if (!Number.isFinite(value)) return '$0';
  if (value === 0) return '$0';
  return `$${value.toLocaleString()}`;
};

export const formatCount = (num) => Number(num || 0).toLocaleString();
